import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/PageUI";
import { LiveConsole } from "@/components/LiveConsole";
import { Radio, LogOut, Trophy, Flag } from "lucide-react";

const StatusPill = ({ s }) => {
  if (s === "live") return <span className="inline-flex items-center gap-1 text-xs font-bold text-red-500"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> LIVE</span>;
  if (s === "completed") return <span className="text-xs font-semibold text-emerald-500">Full time</span>;
  return <span className="text-xs text-muted-foreground">Scheduled</span>;
};

export default function Officials() {
  const { user, logout } = useAuth();
  const [assignments, setAssignments] = useState(null);
  const [live, setLive] = useState(null);

  const load = () => api.get("/interclub/my-assignments").then((r) => setAssignments(r.data.assignments)).catch(() => setAssignments([]));
  useEffect(() => { load(); }, []);

  const total = (assignments || []).reduce((n, a) => n + a.matches.length, 0);

  return (
    <div className="min-h-screen bg-background grain" data-testid="officials-page">
      <header className="border-b border-border bg-card/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center text-white"><Flag size={18} /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Match Official</p>
              <h1 className="font-heading font-bold text-lg leading-tight" data-testid="official-name">{user?.name || "Official"}</h1>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="official-logout"><LogOut size={15} className="mr-1.5" /> Sign out</Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        <h2 className="font-heading font-bold text-2xl mb-1">Your matches</h2>
        <p className="text-sm text-muted-foreground mb-6">Open the live console to score the matches you've been assigned to.</p>

        {assignments === null ? <Loader /> : total === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="officials-empty">
            <Trophy size={22} className="mx-auto text-muted-foreground mb-2" />
            <p className="font-medium">No matches assigned yet</p>
            <p className="text-sm text-muted-foreground mt-1">A tournament organizer will assign matches to you. Check back soon.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {assignments.map((a) => (
              <div key={a.tournament.id} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`assign-tournament-${a.tournament.id}`}>
                <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                  <Trophy size={15} className="text-primary" />
                  <span className="font-heading font-semibold">{a.tournament.name}</span>
                  <span className="text-xs text-muted-foreground capitalize">· {a.tournament.sport}</span>
                </div>
                <div className="divide-y divide-border">
                  {a.matches.map((m) => (
                    <div key={m.id} className="px-5 py-3 flex items-center gap-3" data-testid={`assign-match-${m.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{m.team_a_name} <span className="text-muted-foreground">vs</span> {m.team_b_name}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <StatusPill s={m.status} />
                          <span className="text-xs text-muted-foreground">{m.round_name || (m.group ? `Group ${m.group}` : "")}</span>
                        </div>
                      </div>
                      <span className="font-heading font-bold tabular-nums">{m.score_a ?? 0} - {m.score_b ?? 0}</span>
                      <Button size="sm" className="brand-gradient text-white border-0" onClick={() => setLive({ tid: a.tournament.id, sport: a.tournament.sport, match: m })} data-testid={`score-match-${m.id}`}>
                        <Radio size={14} className="mr-1.5" /> Score
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {live && <LiveConsole tid={live.tid} match={live.match} sport={live.sport} onClose={() => setLive(null)} onChanged={load} />}
    </div>
  );
}
